import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Empty, useTheme } from '@prio365/prio365-react-library';
import moment from 'moment';
import { TaskSummaryDto } from '../TaskHubModule';
import { ExtendedTaskDto } from '../api/taskHubApi';
import { useChartStyles } from './chartStyles';

interface ProjectBarChartProps {
  summary: TaskSummaryDto;
  allTasks: ExtendedTaskDto[];
  onProjectClick: (projectName: string) => void;
  maxItems?: number;
  selectedProjects?: string[];
}

export const ProjectBarChart: React.FC<ProjectBarChartProps> = ({
  summary,
  allTasks,
  onProjectClick,
  maxItems = 15,
  selectedProjects = [],
}) => {
  const classes = useChartStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const getStatusBarColor = useCallback(
    (status: string): string => {
      switch (status) {
        case 'completed':
          return theme.colors.base.green.default;
        case 'overdue':
          return theme.colors.base.red.default;
        case 'today':
          return theme.colors.base.yellow.default;
        case 'onTrack':
          return theme.colors.base.blue.default;
        default:
          return theme.colors.application.typography.muted;
      }
    },
    [theme]
  );

  const getProjectStatusBreakdown = useCallback(
    (projectName: string) => {
      const projectTasks = allTasks.filter(
        (task) => (task.projectDisplayName || 'Unknown Project') === projectName
      );

      const completed = projectTasks.filter(
        (t) => t.status === 'completed'
      ).length;
      const overdue = projectTasks.filter(
        (t) => t.dueDate && new Date(t.dueDate) < new Date()
      ).length;
      const today = projectTasks.filter(
        (t) => t.dueDate && moment(t.dueDate).isSame(moment(), 'day')
      ).length;
      const onTrack = projectTasks.length - completed - overdue - today;

      return { completed, overdue, today, onTrack, total: projectTasks.length };
    },
    [allTasks]
  );

  const projectData = useMemo(() => {
    return Object.entries(summary.tasksByProject)
      .sort(([, a], [, b]) => b - a)
      .slice(0, maxItems)
      .map(([project, totalTasks]) => {
        const breakdown = getProjectStatusBreakdown(project);
        const isSelected = selectedProjects.includes(project);
        const bars = [
          {
            fraction: breakdown.completed,
            color: getStatusBarColor('completed'),
            title: t('taskHub:dashboard.charts.statusTypes.completed'),
            content: `${breakdown.completed} ${breakdown.completed === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.onTrack,
            color: getStatusBarColor('onTrack'),
            title: t('taskHub:dashboard.charts.statusTypes.onTrack'),
            content: `${breakdown.onTrack} ${breakdown.onTrack === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.today,
            color: getStatusBarColor('today'),
            title: t('taskHub:dashboard.charts.statusTypes.today'),
            content: `${breakdown.today} ${breakdown.today === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
          {
            fraction: breakdown.overdue,
            color: getStatusBarColor('overdue'),
            title: t('taskHub:dashboard.charts.statusTypes.overdue'),
            content: `${breakdown.overdue} ${breakdown.overdue === 1 ? t('taskHub:dashboard.charts.taskSingular') : t('taskHub:dashboard.charts.taskPlural')}`,
          },
        ].filter((bar) => bar.fraction > 0);

        return {
          project,
          totalTasks,
          bars,
          breakdown,
          isSelected,
        };
      });
  }, [
    summary.tasksByProject,
    maxItems,
    getProjectStatusBreakdown,
    selectedProjects,
    t,
    getStatusBarColor,
  ]);

  return (
    <div className={classes.scrollableChartCard}>
      <h3 className={classes.cardTitle}>
        {t('taskHub:dashboard.charts.tasksByProject')}
      </h3>
      <div className={classes.scrollableContainer}>
        {projectData.map(
          ({ project, totalTasks, bars, breakdown, isSelected }) => (
            <div
              key={project}
              className={classes.chartItem}
              onClick={() => onProjectClick(project)}
              style={{
                backgroundColor: isSelected
                  ? theme.colors.base.primary.default + '10'
                  : 'transparent',
                borderLeft: isSelected
                  ? `3px solid ${theme.colors.base.primary.default}`
                  : '3px solid transparent',
              }}
            >
              <div className={classes.chartLabel}>
                <span
                  title={project}
                  style={{
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected
                      ? theme.colors.base.primary.default
                      : theme.colors.application.typography.default,
                  }}
                >
                  {project}
                  {isSelected && ' ✓'}
                </span>
                <span className={classes.taskCount}>
                  {totalTasks}{' '}
                  {totalTasks !== 1
                    ? t('taskHub:dashboard.charts.taskPlural')
                    : t('taskHub:dashboard.charts.taskSingular')}
                </span>
              </div>
              {bars.length > 0 ? (
                <div className={classes.barChartContainer}>
                  <BarChart bars={bars} />
                </div>
              ) : (
                <div className={classes.emptyBarPlaceholder}>
                  {t('taskHub:dashboard.charts.noTaskDataAvailable')}
                </div>
              )}
            </div>
          )
        )}
        {projectData.length === 0 && (
          <Empty
            customMessage={t('taskHub:dashboard.charts.noProjectDataAvailable')}
          />
        )}
      </div>
    </div>
  );
};
